/**
 * ARTER Custom Product Options v2.1
 * With IMAGE-BASED Color Swatches
 * Fixed: Updates ALL color labels on the page
 * 
 * INSTALLATION:
 * 1. Go to Shopify Admin > Online Store > Themes > Edit Code
 * 2. Open "Layout" > theme.liquid
 * 3. Find </head> tag and paste this BEFORE it:
 *    {{ 'arter-options.css' | asset_url | stylesheet_tag }}
 * 4. Find </body> tag and paste this BEFORE it:
 *    {{ 'arter-options.js' | asset_url | script_tag }}
 * 5. Upload this file to Assets folder as "arter-options.js"
 * 6. Upload the CSS file to Assets folder as "arter-options.css"
 * 7. Upload your swatch images to Assets folder (see IMAGE NAMING below)
 * 
 * IMAGE NAMING CONVENTION:
 * Upload swatch images with this naming format:
 *   swatch-{color-handle}.jpg (or .png, .webp)
 * 
 * Examples:
 *   - "Chocolate High" → swatch-chocolate-high.jpg
 *   - "Scandinavian Navy Blue" → swatch-scandinavian-navy-blue.jpg
 */

(function() {
  'use strict';

  const CONFIG = {
    swatchImages: {
      basePath: '',
      extension: 'jpg',
      fallbackToColor: true
    },
    
    colorMap: {
      'chocolate-high': '#5C4033',
      'chocolate': '#5C4033',
      'scandinavian-navy-blue': '#1B365D',
      'navy-blue': '#1B365D',
      'signature-champagne': '#F7E7CE',
      'champagne': '#F7E7CE',
      'blissful-white': '#FAF9F6',
      'broken-white': '#FAF9F6',
      'white': '#FFFFFF',
      'charcoal-grey': '#36454F',
      'charcoal-gray': '#36454F',
      'charcoal': '#36454F',
      'luxury-bliss-silver': '#C0C0C0',
      'silver': '#C0C0C0',
      'butterscotch': '#E09540',
      'tranquil-lotus': '#F4C2C2',
      'lotus': '#F4C2C2',
      'kiss-from-a-rose': '#FF007F',
      'rose': '#FF007F',
      'sweet-memory': '#FFB6C1',
      'under-the-sea': '#006994',
      'sea': '#006994',
      'garden-of-flower': '#98D8C8',
      'black': '#1a1a1a',
      'hitam': '#1a1a1a',
      'kopi-panas': '#6F4E37',
      'lilac-garden': '#C8A2C8',
      'lilac': '#C8A2C8'
    },
    
    swatchOptions: ['color', 'colour', 'warna'],
    dropdownOptions: ['size', 'weight', 'berat', 'ukuran'],
    
    weightDescriptions: {
      'low': '40-54 kg',
      'medium': '55-70 kg', 
      'high': '71-90 kg'
    },
    
    selectors: {
      productForm: 'form[action*="/cart/add"]',
      variantSelect: 'select[name="id"]',
      optionSelects: '.product-form__input select, .single-option-selector, [data-single-option-selector]',
      priceElement: '.product__price, .price, [data-product-price]',
      addToCartBtn: '[name="add"], .product-form__submit, .add-to-cart, button[type="submit"]'
    }
  };

  class ArterProductOptions {
    constructor() {
      this.productForm = document.querySelector(CONFIG.selectors.productForm);
      if (!this.productForm) return;
      
      this.variantSelect = this.productForm.querySelector(CONFIG.selectors.variantSelect);
      this.variants = this.getVariantsFromPage();
      this.options = this.parseProductOptions();
      this.shopDomain = this.getShopDomain();
      
      // Store reference to all color-related elements on the page
      this.colorDisplayElements = this.findAllColorDisplayElements();
      
      if (this.options.length > 0) {
        this.init();
      }
    }

    getShopDomain() {
      const img = document.querySelector('img[src*="cdn.shopify.com"]');
      if (img) {
        const match = img.src.match(/(https:\/\/[^\/]+\/s\/files\/[^\/]+)/);
        if (match) return match[1];
      }
      return `https://cdn.shopify.com/s/files/1/0336/5928/9645`;
    }

    getSwatchImageUrl(colorName) {
      const handle = this.toHandle(colorName);
      const ext = CONFIG.swatchImages.extension;
      return `${this.shopDomain}/files/swatch-${handle}.${ext}`;
    }

    toHandle(str) {
      return str
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    getVariantsFromPage() {
      const scriptTag = document.querySelector('[data-product-json], script[type="application/json"][data-product]');
      if (scriptTag) {
        try {
          const data = JSON.parse(scriptTag.textContent);
          return data.variants || data.product?.variants || [];
        } catch (e) {}
      }
      
      if (this.variantSelect) {
        return Array.from(this.variantSelect.options).map(opt => ({
          id: parseInt(opt.value),
          title: opt.textContent.trim(),
          available: !opt.disabled,
          options: this.parseOptionsFromTitle(opt.textContent)
        }));
      }
      
      return [];
    }

    parseOptionsFromTitle(title) {
      return title.split(' / ').map(s => s.trim().split(' - ')[0]);
    }

    parseProductOptions() {
      const options = [];
      const selects = this.productForm.querySelectorAll(CONFIG.selectors.optionSelects);
      
      selects.forEach((select, index) => {
        const label = this.findLabelForSelect(select);
        const name = label ? label.textContent.trim().replace(/\s*:.*$/, '').replace(/\s+/g, ' ') : `Option ${index + 1}`;
        const values = Array.from(select.options).map(opt => opt.value).filter(v => v);
        
        options.push({
          name: name,
          position: index,
          values: values,
          element: select,
          type: this.getOptionType(name)
        });
      });
      
      return options;
    }

    findLabelForSelect(select) {
      const id = select.id;
      if (id) {
        const label = document.querySelector(`label[for="${id}"]`);
        if (label) return label;
      }
      
      const parent = select.closest('.product-form__input, .selector-wrapper, .option-selector');
      if (parent) {
        return parent.querySelector('label, .form__label');
      }
      
      return null;
    }

    getOptionType(name) {
      const nameLower = name.toLowerCase();
      
      if (CONFIG.swatchOptions.some(s => nameLower.includes(s))) {
        return 'swatch';
      }
      if (CONFIG.dropdownOptions.some(s => nameLower.includes(s))) {
        return 'dropdown';
      }
      return 'default';
    }

    /**
     * Find ALL elements on the page that display the selected color
     * This includes elements from VO Product Options or theme
     */
    findAllColorDisplayElements() {
      const elements = [];
      
      // Common selectors for color display text
      const selectors = [
        // VO Product Options selectors
        '.vo-option-label-value',
        '.vo-selected-value',
        '[data-option-value]',
        '.product-option__value',
        
        // Common theme selectors
        '.selected-option',
        '.variant-option__selected',
        '.option-value',
        
        // Elements containing "COLOR" text followed by value
        '.product-single__meta span',
        '.product__meta span',
        
        // Our own selector
        '.arter-selected-color'
      ];
      
      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          elements.push(el);
        });
      });
      
      // Also find any element that looks like it's displaying a color value
      // Look for elements near "COLOR" or "Color" labels
      document.querySelectorAll('*').forEach(el => {
        const text = el.textContent?.trim() || '';
        const prevSibling = el.previousElementSibling;
        const parent = el.parentElement;
        
        // Check if this element is right after a "COLOR" label
        if (prevSibling) {
          const prevText = prevSibling.textContent?.trim().toUpperCase() || '';
          if (prevText === 'COLOR' || prevText === 'COLOUR' || prevText === 'WARNA') {
            if (!elements.includes(el)) {
              elements.push(el);
            }
          }
        }
        
        // Check if parent contains "COLOR" and this is the value
        if (parent) {
          const parentText = parent.textContent?.trim() || '';
          const parentLabel = parent.querySelector('strong, b, span:first-child');
          if (parentLabel) {
            const labelText = parentLabel.textContent?.trim().toUpperCase() || '';
            if ((labelText === 'COLOR' || labelText === 'COLOUR' || labelText === 'WARNA') && el !== parentLabel) {
              if (!elements.includes(el)) {
                elements.push(el);
              }
            }
          }
        }
      });
      
      return elements;
    }

    /**
     * Update all color display elements on the page
     */
    updateAllColorDisplays(colorName) {
      // Update our own label
      const ourLabel = document.querySelector('.arter-selected-color');
      if (ourLabel) {
        ourLabel.textContent = colorName;
      }
      
      // Find and update the VO Product Options / theme color display
      // Look for elements that contain current color names
      const allColors = this.options.find(o => o.type === 'swatch')?.values || [];
      
      document.querySelectorAll('*').forEach(el => {
        // Skip script, style, and our own elements
        if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.classList.contains('arter-swatch')) {
          return;
        }
        
        const text = el.textContent?.trim() || '';
        
        // Check if this element contains exactly a color name (not nested)
        if (allColors.includes(text) && el.children.length === 0) {
          // Check if parent/nearby element suggests this is a color label
          const parent = el.parentElement;
          const grandparent = parent?.parentElement;
          const context = (parent?.textContent || '') + (grandparent?.textContent || '');
          
          if (context.toLowerCase().includes('color') || 
              context.toLowerCase().includes('colour') || 
              context.toLowerCase().includes('warna')) {
            el.textContent = colorName;
          }
        }
      });
      
      // Also specifically target the bottom COLOR label structure you showed
      // It appears to be: <strong>COLOR</strong> followed by text or span
      document.querySelectorAll('strong, b').forEach(label => {
        const labelText = label.textContent?.trim().toUpperCase();
        if (labelText === 'COLOR' || labelText === 'COLOUR' || labelText === 'WARNA') {
          const parent = label.parentElement;
          if (parent) {
            // Find the text node or span after the label
            const nextSibling = label.nextSibling;
            if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE) {
              nextSibling.textContent = ' ' + colorName;
            } else if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE) {
              nextSibling.textContent = colorName;
            } else {
              // Try to find a span sibling
              const siblingSpan = parent.querySelector('span:not(:first-child)');
              if (siblingSpan && allColors.includes(siblingSpan.textContent?.trim())) {
                siblingSpan.textContent = colorName;
              }
            }
          }
        }
      });
    }

    init() {
      this.options.forEach(option => {
        if (option.type === 'swatch') {
          this.createImageSwatches(option);
        } else if (option.type === 'dropdown') {
          this.styleDropdown(option);
        }
      });
      
      // Hide VO Product Options elements if they exist
      this.hideVOProductOptions();
    }

    /**
     * Hide VO Product Options app elements to avoid duplication
     */
    hideVOProductOptions() {
      const voSelectors = [
        '.vo-product-options',
        '.vo-options-wrapper',
        '[data-vo-product-options]',
        '.product-options-vo'
      ];
      
      voSelectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          // Only hide if our swatches are successfully rendered
          if (document.querySelector('.arter-color-swatches')) {
            el.style.display = 'none';
          }
        });
      });
    }

    createImageSwatches(option) {
      const select = option.element;
      const parent = select.closest('.product-form__input, .selector-wrapper, .option-selector') || select.parentElement;
      
      // Check if already created
      if (parent.querySelector('.arter-color-swatches')) return;
      
      const swatchContainer = document.createElement('div');
      swatchContainer.className = 'arter-color-swatches arter-image-swatches';
      swatchContainer.setAttribute('role', 'radiogroup');
      swatchContainer.setAttribute('aria-label', option.name);
      
      // Create selected color display
      const selectedDisplay = document.createElement('span');
      selectedDisplay.className = 'arter-selected-color';
      selectedDisplay.textContent = select.value;
      
      const label = this.findLabelForSelect(select);
      if (label && !label.querySelector('.arter-selected-color')) {
        label.appendChild(selectedDisplay);
      }
      
      // Store reference for updates
      this.selectedColorDisplay = selectedDisplay;
      this.colorValues = option.values;
      
      option.values.forEach((value, index) => {
        const swatch = this.createImageSwatch(value, select.value === value, index);
        
        swatch.addEventListener('click', () => {
          // Update native select
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          
          // Update swatch UI
          swatchContainer.querySelectorAll('.arter-swatch').forEach(s => {
            s.classList.remove('is-selected');
            s.setAttribute('aria-checked', 'false');
          });
          swatch.classList.add('is-selected');
          swatch.setAttribute('aria-checked', 'true');
          
          // Update ALL color displays on the page (including VO/theme ones)
          this.updateAllColorDisplays(value);
        });
        
        swatchContainer.appendChild(swatch);
      });
      
      select.style.display = 'none';
      select.setAttribute('aria-hidden', 'true');
      parent.appendChild(swatchContainer);
    }

    createImageSwatch(colorName, isSelected, index) {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = `arter-swatch arter-swatch-image${isSelected ? ' is-selected' : ''}`;
      swatch.setAttribute('role', 'radio');
      swatch.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      swatch.setAttribute('aria-label', colorName);
      swatch.setAttribute('title', colorName);
      swatch.style.setProperty('--swatch-index', index);
      
      const inner = document.createElement('span');
      inner.className = 'arter-swatch-inner';
      
      const img = document.createElement('img');
      img.className = 'arter-swatch-img';
      img.alt = colorName;
      img.loading = 'lazy';
      img.src = this.getSwatchImageUrl(colorName);
      
      img.onload = () => {
        img.classList.add('loaded');
      };
      
      img.onerror = () => {
        if (CONFIG.swatchImages.fallbackToColor) {
          img.style.display = 'none';
          const handle = this.toHandle(colorName);
          const fallbackColor = CONFIG.colorMap[handle] || '#ccc';
          inner.style.backgroundColor = fallbackColor;
          inner.classList.add('arter-swatch-color-fallback');
        }
      };
      
      inner.appendChild(img);
      
      const tooltip = document.createElement('span');
      tooltip.className = 'arter-swatch-tooltip';
      tooltip.textContent = colorName;
      
      swatch.appendChild(inner);
      swatch.appendChild(tooltip);
      
      return swatch;
    }

    styleDropdown(option) {
      const select = option.element;
      const parent = select.closest('.product-form__input, .selector-wrapper, .option-selector') || select.parentElement;
      
      if (parent.querySelector('.arter-select-wrapper')) return;
      
      const wrapper = document.createElement('div');
      wrapper.className = 'arter-select-wrapper';
      
      select.classList.add('arter-weight-select');
      
      Array.from(select.options).forEach(opt => {
        if (!opt.value) return;
        const valueLower = opt.value.toLowerCase();
        const desc = CONFIG.weightDescriptions[valueLower];
        if (desc && !opt.textContent.includes(desc)) {
          opt.textContent = `${opt.value} (${desc})`;
        }
      });
      
      const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      arrow.setAttribute('class', 'arter-select-arrow');
      arrow.setAttribute('viewBox', '0 0 20 20');
      arrow.setAttribute('fill', 'currentColor');
      arrow.innerHTML = '<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>';
      
      select.parentNode.insertBefore(wrapper, select);
      wrapper.appendChild(select);
      wrapper.appendChild(arrow);
    }
  }

  function init() {
    setTimeout(() => {
      new ArterProductOptions();
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('turbo:load', init);
  document.addEventListener('turbolinks:load', init);
})();